import { chaveDeDataDeCalendario } from '@/shared/formatters';
import { generateUuid } from '@/shared/utils';

import type {
  ArbitragemPendencia,
  ArbitragemPendenciaResolucao,
  ArbitragemPendenciaResolucaoResultado,
} from '../types/arbitragemDetalheTypes';

import type {
  CitacaoArbitralDetail,
  CitacaoArbitralSubmitPayload,
  CitacaoArbitralSubmitResponse,
  CitacaoArbitralUploadResult,
  PortalFeedExtrajudicialListItem,
} from '../types/portalArbitralTypes';

// TODO: remover junto com o mock de complementos quando o backend expuser
// os processos arbitrais do notificado no feed do portal.
export const portalMockArbitragensAtivo = (): boolean =>
  process.env.NEXT_PUBLIC_PORTAL_MOCK_ARBITRAGENS === 'true';

// Prazo relativo, não data fixa: a fixture anterior (`2026-08-20T23:59:00.000Z`) expirou
// sozinha e derrubou o gate `test` do CI, porque o caso existe justamente para exercitar
// "prazo em aberto" e virou "prazo perdido" quando o relógio passou dele.
const prazoAbertoDoMock = (): string => new Date(Date.now() + 10 * 86_400_000).toISOString();

/**
 * Instante e dia do MESMO prazo. Derivar cada campo de uma chamada própria os faria divergir
 * quando a avaliação cruzasse a virada do dia em SP — a divergência que o mock deveria ilustrar
 * corrigida, não reproduzir.
 */
const PRAZO_ABERTO = prazoAbertoDoMock();

const MOCK_ARBITRAGENS: CitacaoArbitralDetail[] = [
  {
    id: 'cma0f7k2x0001qsd8n3vph2la',
    valorCobrado: 2500,
    tipo: 'ARBITRAL_CITACAO',
    remetente: { nomeFantasia: 'Arbitralis', logotipo: null },
    numeroProcesso: 'B83871507261522',
    requerenteNome: 'Bianca Requerente',
    requeridoNome: 'Jonathan Jader Ferreira da Silva',
    arbitroNome: 'Dr. Paulo Andrade',
    impugnacaoNoPrazo: true,
    status: 'AGUARDANDO_LEITURA',
    prazoDataLimite: null,
    prazoDataLimiteDia: null,
    jaSubmeteu: false,
    dataCitacao: '2026-07-15T18:22:00.000Z',
    processoEncerrado: false,
    manifestacaoExistente: null,
    canRespond: true,
    etapaAtual: 2,
    esclarecimento: null,
  },
  {
    id: 'cma0f8m5y0002qsd8r7bt9k1c',
    valorCobrado: 1250,
    tipo: 'ARBITRAL_CITACAO',
    remetente: { nomeFantasia: 'Arbitralis', logotipo: null },
    numeroProcesso: 'B71120906261140',
    requerenteNome: 'Construtora Vale Verde',
    requeridoNome: 'Jonathan Jader Ferreira da Silva',
    arbitroNome: 'Dr. Paulo Andrade',
    impugnacaoNoPrazo: true,
    status: 'EM_TRATATIVA',
    prazoDataLimite: PRAZO_ABERTO,
    prazoDataLimiteDia: chaveDeDataDeCalendario(PRAZO_ABERTO),
    jaSubmeteu: false,
    dataCitacao: '2026-06-09T14:40:00.000Z',
    processoEncerrado: false,
    manifestacaoExistente: null,
    canRespond: true,
    etapaAtual: 2,
    esclarecimento: null,
  },
  {
    id: 'cma0f9p8z0003qsd8e4rn6d0m',
    valorCobrado: 7480.9,
    tipo: 'ARBITRAL_CITACAO',
    remetente: { nomeFantasia: 'Arbitralis', logotipo: null },
    numeroProcesso: 'B44030204260918',
    requerenteNome: 'Imobiliária Santa Clara',
    requeridoNome: 'Jonathan Jader Ferreira da Silva',
    arbitroNome: 'Dr. Paulo Andrade',
    status: 'CONCLUIDO',
    prazoDataLimite: '2026-05-02T23:59:00.000Z',
    prazoDataLimiteDia: '2026-05-02',
    jaSubmeteu: true,
    dataCitacao: '2026-04-02T09:18:00.000Z',
    processoEncerrado: true,
    manifestacaoExistente: {
      id: 'mock-manifestacao-1',
      dataProtocolo: '2026-04-28T16:05:00.000Z',
      texto: 'Apresentei minha versão dos fatos e anexei os comprovantes de pagamento.',
      anexos: [],
    },
    canRespond: false,
    etapaAtual: 7,
    esclarecimento: null,
  },
];

// Usadas só quando o feed real falha, para a divisória entre processos e
// notificações ficar revisável. Feed real respondendo, a lista verdadeira vence.
const MOCK_NOTIFICACOES: PortalFeedExtrajudicialListItem[] = [
  {
    id: 'mock-notificacao-aberta',
    remetente: { nomeFantasia: 'Clínica Bem Viver', logotipo: null },
    breveRelato: 'Mensalidades de março e abril em aberto.',
    numeroNotificacao: 'NT-2026-004512',
    valor: 890.4,
    dataRecebimento: '2026-07-28T13:10:00.000Z',
    dataExpiracaoNotificacao: '2026-08-27T23:59:00.000Z',
    status: 'AGUARDANDO_LEITURA',
    statusLabel: 'Aguardando leitura',
    badgeVariant: 'warning',
    badgeLabel: 'Nova',
    foiLida: false,
    comprovanteEnviado: false,
    negociacaoAberta: false,
  },
  {
    id: 'mock-notificacao-negociando',
    remetente: { nomeFantasia: 'Auto Peças Trindade', logotipo: null },
    breveRelato: 'Parcelas do acordo de compra de peças.',
    numeroNotificacao: 'NT-2026-004188',
    valor: 1560,
    dataRecebimento: '2026-07-02T10:25:00.000Z',
    dataExpiracaoNotificacao: null,
    status: 'EM_NEGOCIACAO',
    statusLabel: 'Em negociação',
    badgeVariant: 'brand',
    badgeLabel: 'Em negociação',
    foiLida: true,
    comprovanteEnviado: false,
    negociacaoAberta: true,
  },
];

export const listarArbitragensMock = (): CitacaoArbitralDetail[] => MOCK_ARBITRAGENS;

export const listarNotificacoesMock = (): PortalFeedExtrajudicialListItem[] => MOCK_NOTIFICACOES;

export const buscarArbitragemMock = (id: string): CitacaoArbitralDetail | null =>
  MOCK_ARBITRAGENS.find((arbitragem) => arbitragem.id === id) ?? null;

export const ehArbitragemMock = (id: string): boolean =>
  portalMockArbitragensAtivo() && buscarArbitragemMock(id) !== null;

const ATRASO_SUBMISSAO_MS = 1400;
const ATRASO_UPLOAD_MS = 700;

const aguardar = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const submeterManifestacaoMock = async (
  payload: CitacaoArbitralSubmitPayload,
): Promise<CitacaoArbitralSubmitResponse> => {
  await aguardar(ATRASO_SUBMISSAO_MS);
  return {
    manifestacaoId: `mock-manifestacao-${generateUuid()}`,
    andamentoId: `mock-andamento-${generateUuid()}`,
    submissionId: payload.submissionId,
    dataProtocolo: new Date().toISOString(),
  };
};

const ATRASO_RESOLVER_PENDENCIA_MS = 700;

export const resumirResolucaoPendencia = (resolucao: ArbitragemPendenciaResolucao): string => {
  const total = resolucao.arquivosNomes.length;
  if (total === 1) return '1 arquivo enviado';
  if (total > 1) return `${total} arquivos enviados`;
  return resolucao.texto ? 'resposta por escrito' : 'resposta enviada';
};

// TODO: remover quando existir POST /arbitral/portal/citacoes/:id/pendencias/:pendenciaId.
export const resolverPendenciaMock = async (
  pendencia: ArbitragemPendencia,
  resolucao: ArbitragemPendenciaResolucao,
): Promise<ArbitragemPendenciaResolucaoResultado> => {
  await aguardar(ATRASO_RESOLVER_PENDENCIA_MS);
  const resolvidaEmIso = new Date().toISOString();
  const resumoResolucao = resumirResolucaoPendencia(resolucao);

  return {
    resolvida: {
      id: pendencia.id,
      titulo: pendencia.titulo,
      resolvidaEmIso,
      resumoResolucao,
    },
    andamento: {
      id: `mock-andamento-pendencia-${generateUuid()}`,
      numero: 0,
      ocorridoEmIso: resolvidaEmIso,
      titulo: `Você resolveu: ${pendencia.titulo}`,
      descricao: `Resposta a ${pendencia.origemRotulo} · ${resumoResolucao}.`,
      autor: 'Você',
      icone: 'confirmacao',
      documento: null,
    },
  };
};

export const uploadAnexoMock = async (file: File): Promise<CitacaoArbitralUploadResult> => {
  await aguardar(ATRASO_UPLOAD_MS);
  return {
    name: file.name,
    path: `mock-anexos/${generateUuid()}/${file.name}`,
    type: file.type,
    arquivoDigitalId: `mock-arquivo-digital-${generateUuid()}`,
  };
};
